import { ReactNode, useContext } from "react";
import { URLSearchParamsInit, RouterContext } from "@amzn/xb-hooks";
import { createSearchParams, useSearchParams } from "./useSearchParams";
import { useOnRouteChange } from "../../hooks/useOnRouteChange";
import { RouterConfiguration } from "./types";

/*
 * The router provider receieves a host applications' client-side routing utilities, including a push function, replace function,
 * and a <Link> component. Those are wrapped, and their functionality is exposed via a common interface, supporting NextJS's
 * client-side router and react-router under the hood.
 */
export const RoutingProvider = (
  props: {
    children: ReactNode;
  } & RouterConfiguration,
) => {
  const { children, push, replace, Link } = props;

  useOnRouteChange();

  const setSearchParams = (params: URLSearchParamsInit) => {
    if (typeof window !== "undefined") {
      const search = window.location.search;
      const currentSearchParams = new URLSearchParams(search);
      const searchParamsToUpdate = createSearchParams(params);
      if (searchParamsToUpdate) {
        searchParamsToUpdate.forEach((value, key) =>
          value !== "" ? currentSearchParams.set(key, value) : currentSearchParams.delete(key),
        );
      }
      const basePath = window.location.pathname;
      push(`${basePath}?${currentSearchParams}`);
    } else {
      console.warn("setSearchParams failure: Cannot use client-side routing functionality on the server.");
    }
  };

  const back = () => window.history.back();
  const reload = () => window.location.reload();

  return (
    <RouterContext.Provider value={{ push, replace, back, reload, Link, useSearchParams, setSearchParams }}>
      {children}
    </RouterContext.Provider>
  );
};

export const useRouter = () => useContext(RouterContext);
